import React, { useState } from "react";
import {
  useCustomMutation,
  useGo,
  useInvalidate,
  useTranslate,
} from "@refinedev/core";
import { Drawer, typography, radius } from "@libertify/ui";
import { Input, Tooltip } from "antd";
import {
  ExportIcon,
  HeadCircuitIcon,
  HighlighterCircleIcon,
  PencilSimpleIcon,
  PlayCircleIcon,
} from "@phosphor-icons/react";
import { createGlobalStyle } from "styled-components";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { isProjectHeyGenius } from "../helpers/helper";
import { ProjectTagList } from "./ProjectTagList";
import { AudienceViewSection } from "./drawer/AudienceViewSection";
import { LinksCtaSection } from "./drawer/LinksCtaSection";
import { AnalyticsMiniSection } from "./drawer/AnalyticsMiniSection";
import { ChatHistorySection } from "./drawer/ChatHistorySection";
import {
  DSection,
  DSectionLabel,
  TitleEditRow,
  TitleText,
  DSaveBtn,
  EditIconBtn,
  FooterColumn,
  ShareFooterBtn,
  ExportFooterBtn,
} from "./drawer/drawer-styled";
import type { ProjectPageDetails } from "./ProjectPage";

export type ProjectSettingsDrawerProps = {
  open: boolean;
  onClose: () => void;
  projectDetails: ProjectPageDetails;
  refetchProjectDetail: () => void;
  onShareClick?: () => void;
  chatEnabled: boolean;
  documentEnabled: boolean;
  onChatToggle: (checked: boolean) => void;
  onDocumentToggle: (checked: boolean) => void;
  onPreviewRefresh?: () => void;
  isMediaReady: boolean;
};

const MobileDrawerStyles = createGlobalStyle`
  .mobile-settings-drawer .ant-drawer-content-wrapper {
    border-radius: ${radius.box}px ${radius.box}px 0 0 !important;
    overflow: hidden;
  }
  .mobile-settings-drawer .ant-drawer-content {
    border-radius: ${radius.box}px ${radius.box}px 0 0 !important;
  }
  .mobile-settings-drawer .ant-drawer-header {
    border-radius: ${radius.box}px ${radius.box}px 0 0 !important;
    flex-shrink: 0 !important;
  }
  .mobile-settings-drawer .ant-drawer-body {
    overflow-y: auto !important;
    padding: 0 !important;
  }
`;

export const ProjectSettingsDrawer: React.FC<ProjectSettingsDrawerProps> = ({
  open,
  onClose,
  projectDetails,
  refetchProjectDetail,
  onShareClick,
  chatEnabled,
  documentEnabled,
  onChatToggle,
  onDocumentToggle,
  onPreviewRefresh,
  isMediaReady,
}) => {
  const t = useTranslate();
  const go = useGo();
  const { isMobile } = useIsMobile();
  const { mutateAsync } = useCustomMutation();
  const invalidate = useInvalidate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const {
    project,
    projectId,
    instanceId,
    storyboardId,
    media,
    creationDate,
  } = projectDetails;

  const isPromptProject =
    project.documents[0]?.retrieval?.provider.type ===
    "DocumentGeneratorProvider";

  const showFullSettings = !isProjectHeyGenius(project.type);

  const updateProjectTitle = async (title: string) => {
    await mutateAsync({
      url: `media/projects/${projectId}`,
      method: "patch",
      values: { title },
    });
    await invalidate({
      resource: "media/projects",
      id: projectId,
      invalidates: ["detail"],
    });
    refetchProjectDetail();
  };

  const handleTitleEdit = () => {
    setTitleDraft(project.title);
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== project.title) {
      void updateProjectTitle(trimmed);
    }
    setIsEditingTitle(false);
  };

  return (
    <>
      {isMobile && <MobileDrawerStyles />}
      <Drawer
        open={open}
        onClose={onClose}
        zIndex={1100}
        title={
          <span style={{ fontSize: 15, fontWeight: 700, color: "#090e19" }}>
            {t(
              "media.components.ProjectSettingsDrawer.title",
              "Experience Settings"
            )}
          </span>
        }
        placement={isMobile ? "bottom" : "right"}
        width={isMobile ? undefined : 420}
        height={isMobile ? "85vh" : undefined}
        rootClassName={isMobile ? "mobile-settings-drawer" : undefined}
        destroyOnClose
        styles={{
          header: {
            borderBottom: "1px solid #F3F4F6",
            padding: "16px 24px",
            fontFamily: typography.fontFamily.secondary,
          },
          body: {
            padding: "0",
            overflow: "auto",
            fontFamily: typography.fontFamily.secondary,
          },
          footer: {
            borderTop: "1px solid #F3F4F6",
            padding: "16px 24px",
            fontFamily: typography.fontFamily.secondary,
          },
        }}
        footer={
          <FooterColumn>
            {showFullSettings && (
              <ShareFooterBtn onClick={onShareClick}>
                <ExportIcon size={16} />
                {t(
                  "media.components.ProjectSettingsDrawer.shareVideo",
                  "Share this video experience"
                )}
              </ShareFooterBtn>
            )}
            <ExportFooterBtn
              disabled={!media?.url}
              onClick={() => {
                if (media?.url) {
                  globalThis.open(media.url, "_blank");
                }
              }}
            >
              <ExportIcon size={16} />
              {t(
                "media.components.ProjectSettingsDrawer.exportVideo",
                "Export video"
              )}
            </ExportFooterBtn>
          </FooterColumn>
        }
      >
        {/* Section 1: Title */}
        <DSection>
          <DSectionLabel>
            {t("media.components.ProjectSettingsDrawer.projectTitle", "Title")}
          </DSectionLabel>
          {isEditingTitle ? (
            <TitleEditRow>
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onPressEnter={handleTitleSave}
                autoFocus
                size="small"
              />
              <DSaveBtn onClick={handleTitleSave}>
                {t("buttons.save", "Save")}
              </DSaveBtn>
            </TitleEditRow>
          ) : (
            <TitleEditRow>
              <TitleText>{project.title}</TitleText>
              <EditIconBtn onClick={handleTitleEdit}>
                <PencilSimpleIcon size={14} color="#9CA3AF" />
              </EditIconBtn>
            </TitleEditRow>
          )}
        </DSection>

        {/* Section 2: Analytics */}
        {showFullSettings && (
          <AnalyticsMiniSection
            projectId={projectId}
            creationDate={creationDate}
          />
        )}

        {/* Section 3: Tools */}
        <DSection>
          <DSectionLabel>
            {t("media.components.ProjectSettingsDrawer.tools", "Tools")}
          </DSectionLabel>
          <ExportFooterBtn
            onClick={() => go({ to: `/media/projects/${projectId}/kb` })}
          >
            <HeadCircuitIcon size={16} />
            {t("media.projects.projectMenu.editKbChatbot")}
          </ExportFooterBtn>
          {!isPromptProject && (
            <Tooltip
              title={
                isMediaReady
                  ? ""
                  : t(
                      "media.components.ProjectSettingsDrawer.documentHighlighterDisabledTooltip",
                      "Please wait until the video is generated to edit highlights"
                    )
              }
            >
              <ExportFooterBtn
                disabled={!isMediaReady}
                onClick={() =>
                  go({
                    to: `/media/projects/${projectId}/media/${media?.id}/animation`,
                  })
                }
              >
                <HighlighterCircleIcon size={16} />
                {t(
                  "media.components.ProjectSettingsDrawer.documentHighlighter",
                  "Document Highlighter"
                )}
              </ExportFooterBtn>
            </Tooltip>
          )}
          {project.type !== "flash" && (
            <Tooltip
              title={
                isMediaReady
                  ? ""
                  : t(
                      "media.components.ProjectSettingsDrawer.editScriptDisabledTooltip",
                      "Please wait until the video is generated to edit the script"
                    )
              }
            >
              <ExportFooterBtn
                disabled={!isMediaReady}
                onClick={() =>
                  go({
                    to: `/media/projects/${projectId}/${storyboardId}/${instanceId}/script`,
                  })
                }
              >
                <PlayCircleIcon size={16} />
                {t(
                  "media.components.ProjectSettingsDrawer.editScript",
                  "Edit Script"
                )}
              </ExportFooterBtn>
            </Tooltip>
          )}
        </DSection>

        {showFullSettings && (
          <>
            {/* Section 4: Tags */}
            <DSection>
              <DSectionLabel>
                {t("media.components.ProjectSettingsDrawer.tags", "Tags")}
              </DSectionLabel>
              <ProjectTagList tags={project.tags} projectId={projectId} />
            </DSection>

            {/* Section 5: Audience View */}
            <AudienceViewSection
              projectId={projectId}
              instanceId={instanceId}
              documentEnabled={documentEnabled}
              chatEnabled={chatEnabled}
              onDocumentToggle={onDocumentToggle}
              onChatToggle={onChatToggle}
              onSettingsChange={refetchProjectDetail}
              isPromptProject={isPromptProject}
            />

            {/* Section 6: Links & CTAs */}
            <LinksCtaSection
              projectId={projectId}
              instanceId={instanceId}
              ctaGroupId={media?.cta_group_id}
              onCtaChange={onPreviewRefresh}
            />

            {/* Section 7: Chat History */}
            <ChatHistorySection projectId={projectId} />
          </>
        )}
      </Drawer>
    </>
  );
};
